<template>
  <VueQueryDevtools v-if="!devtoolsDisabled" />
  <div class="kinn-app text-app-black">
    <transition name="fade">
      <v-app>
        <template v-if="!loading && !isLoading && !setupStore.isLoading">
          <BlankLayout v-if="isBlankLayout" />
          <DefaultLayout v-else-if="isAuthenticated" />
          <UISkeletonLoaderSPA v-else-if="isLoggingOut" />
          <BlankLayout v-else />
        </template>
        <template v-else>
          <UISkeletonLoaderSPA />
        </template>
        <TeleportTarget :name="teleportTargets.body" />
        <AppSnackbar />
        <MockedRequestsDisplay :devtools-z-index="devtoolsZIndex" />
        <ModalDialogsRoot />
      </v-app>
    </transition>
  </div>
</template>

<script lang="ts" setup>
import { httpClient } from '@manager/api/http-client';
import { setHttpClient } from '@manager/api/services/auth-service';
import { useCan } from '@manager/composables/use-can';
import { devtoolsZIndex as devtoolsZIndexItem } from '@manager/config/constants';
import ModalDialogsRoot from '@manager/modules/modals/base/ModalDialogsRoot.vue';
import { routeNames } from '@manager/router/route-names';
import type { IPermission } from '@manager/types/permission-types';
import { VueQueryDevtools } from '@tanstack/vue-query-devtools';
import { MockedRequestsDisplay } from 'shared/msw';
import UISkeletonLoaderSPA from 'shared/ui/registry/kinnovis/ui-skeleton-loaders/UISkeletonLoaderSPA.vue';
import { type InjectionKey, computed, onMounted, provide, ref } from 'vue';
import { type RouteLocationRaw, useRoute, useRouter } from 'vue-router';

import { useIntercom } from './composables/use-intercom';
import { usePostHog } from './composables/use-posthog';
import { useRemoteTranslations } from './composables/use-remote-translations';
import { useVueQueryCleanup } from './composables/use-vue-query-cleanup';
import BlankLayout from './layouts/BlankLayout.vue';
import DefaultLayout from './layouts/DefaultLayout.vue';
import AppSnackbar from './modules/snackbar/components/AppSnackbar.vue';
import TeleportTarget from './modules/teleports/components/TeleportTarget.vue';
import { teleportTargets } from './modules/teleports/teleports.config';
import { useAuthStore } from './store/auth-store';
import { usePermissionStore } from './store/permission-store';
import { useSetupStore } from './store/setup-store';
import { useUserSettingsStore } from './store/user-settings-store';

// Set the HTTP client for the auth service to break up circular dependencies
setHttpClient(httpClient);

// keep this initialization here!
useUserSettingsStore();

const router = useRouter();
const route = useRoute();
const permissionStore = usePermissionStore();
const setupStore = useSetupStore();
const authStore = useAuthStore();
const { can } = useCan();

type IUIDetailCardRoutePermissionHandler = (to: RouteLocationRaw) => boolean;

const uiDetailCardRoutePermissionHandlerKey = Symbol.for(
  'kinnovis.uiDetailCardRoutePermissionHandler'
) as InjectionKey<IUIDetailCardRoutePermissionHandler>;

provide(uiDetailCardRoutePermissionHandlerKey, (to: RouteLocationRaw) => {
  const permission = router.resolve(to).meta?.permission;
  return can(permission as IPermission);
});

// The PDF test renderer loads its bundled de/en messages itself
const isPdfTestRoute = computed(
  () =>
    route.name === routeNames.pdfRendererReportTest ||
    route.name === routeNames.pdfRendererReportTestRenderer
);
const { isLoading } = useRemoteTranslations(() => !isPdfTestRoute.value);
useVueQueryCleanup();
const devtoolsZIndex = devtoolsZIndexItem;
const devtoolsDisabled = import.meta.env.VITE_DISABLE_DEVTOOLS === 'true';

const loading = ref(true);
const isAuthenticated = computed(() => {
  return authStore.isAuthenticated && permissionStore.permissions;
});
const isLoggingOut = computed(() => {
  return authStore.isLoggingOut;
});

const layout = computed(() => route.meta?.layout);
const isBlankLayout = computed(() => layout.value === 'blank');

useIntercom();
usePostHog();

onMounted(() => {
  authStore
    .initializeSession()
    .catch(() => {
      authStore.logout({ saveCurrentUrl: true });
    })
    .finally(() => {
      loading.value = false;
    });
});

router.onError((error, to) => {
  const errors = [
    'Failed to fetch dynamically imported module',
    'Importing a module script failed',
  ];
  if (errors.some((e) => error.message.includes(e))) {
    window.location.href = to.fullPath;
  }
});
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}

:global(.tsqd-parent-container) {
  position: relative;
  z-index: v-bind(devtoolsZIndex);
}
</style>
